import React, { useRef, useContext } from "react"
import type { ReactNode } from "react"
import cx from "classnames"

import { OldButton } from "../old-button"
import { PortalNode } from "../portal"
import {
  AlertDialogLabel,
  AlertDialogOverlay,
  AlertDialogDescription,
  AlertDialogContent
} from "@reach/alert-dialog"
import { motion, AnimatePresence } from "framer-motion"
import { ActionProvider, ActionContext } from "./action"

const AnimatedDialogContent = motion.create(AlertDialogContent)

const DEFAULT = "p-6 bg-white rounded-lg shadow font-sans "

type ConfirmModalActionsProps = {
  leastDestructiveRef: React.RefObject<HTMLButtonElement>
  verticalButtons?: boolean
}

export const Actions = ({
  leastDestructiveRef,
  verticalButtons
}: ConfirmModalActionsProps) => {
  const { actions } = useContext(ActionContext)
  if (actions.length === 0) return null

  return (
    <div
      className={cx("mt-6 flex w-full gap-4", { "flex-col": verticalButtons })}
      data-testid="actions"
    >
      {actions.map(({ id, destructive, ...props }) => (
        <OldButton
          key={id}
          ref={destructive ? undefined : leastDestructiveRef}
          size="lg"
          {...props}
          className={cx("flex-grow", props.className, {
            "mt-0": verticalButtons
          })}
        />
      ))}
    </div>
  )
}

export type ConfirmModalProps = {
  /** Body content rendered below the label, typically a description and `Action` buttons. */
  children?: ReactNode
  /** Additional CSS classes applied to the dialog content element. */
  className?: string
  /** Typically a string, but you can use JSX for more customizability. */
  label: ReactNode
  /** Called when the modal requests to be closed. */
  onClose: () => void
  /** When false, clicking the overlay backdrop will not close the modal. */
  dismissable?: boolean
  /** Optional image or illustration to display above the label. */
  picture?: ReactNode
  /** When true, action buttons stack vertically instead of side by side. */
  verticalButtons?: boolean
  /** Test identifier for automated testing. */
  "data-testid"?: string
  /** Overrides the ARIA role of the dialog element. */
  role?: string
  /** Overrides the aria-modal attribute on the dialog element. */
  "aria-modal"?: boolean | "true" | "false"
  /** ID of the element that labels the dialog for screen readers. */
  "aria-labelledby"?: string
  /** ID of the element that describes the dialog for screen readers. */
  "aria-describedby"?: string
  /** Controls whether the dialog is shown. When false, the overlay is not rendered. */
  isOpen?: boolean
}

/**
 * A compact confirmation dialog built on ARIA alert dialog semantics.
 * Use for destructive or irreversible actions that require explicit user consent.
 * Place `Action` sub-components as children to populate the button row.
 */
function ConfirmModal({
  children,
  className,
  label,
  onClose,
  dismissable = true,
  picture,
  verticalButtons = false,
  "data-testid": dataTestId,
  role,
  "aria-modal": ariaModal,
  "aria-labelledby": ariaLabelledBy,
  "aria-describedby": ariaDescribedBy,
  isOpen
}: ConfirmModalProps) {
  const close = useRef<HTMLButtonElement>(null)

  return (
    <AlertDialogOverlay
      className="flex items-center justify-center"
      onDismiss={dismissable ? onClose : undefined}
      leastDestructiveRef={close}
      isOpen={isOpen}
      data-testid={dataTestId}
      role={role}
      aria-modal={ariaModal}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
    >
      <ActionProvider>
        <AnimatePresence>
          <AnimatedDialogContent
            key="confirm-modal-content"
            className={cx(className, DEFAULT)}
            initial={{ opacity: 0, y: -15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
          >
            {picture && <div className="mb-4">{picture}</div>}

            <AlertDialogLabel
              style={{ maxWidth: "22rem" }}
              className="text-lg text-gray-900 font-semibold leading-8"
            >
              {label}
            </AlertDialogLabel>
            <AlertDialogDescription
              style={{ maxWidth: "22rem" }}
              className={cx(
                "text-gray-800 leading-7",
                label ? "text-base mt-2" : "text-lg font-semibold"
              )}
            >
              <PortalNode>{children}</PortalNode>
            </AlertDialogDescription>
            <Actions
              leastDestructiveRef={close}
              verticalButtons={verticalButtons}
            />
          </AnimatedDialogContent>
        </AnimatePresence>
      </ActionProvider>
    </AlertDialogOverlay>
  )
}

export { ConfirmModal }
export default ConfirmModal
